import {
  Box,
  FormControl,
  FormLabel,
  InputProps,
  styled,
  Typography,
  useTheme,
} from "@mui/material";
import {
  Control,
  Controller,
  ControllerProps,
  useFormState,
} from "react-hook-form";
import { StyledInput } from "@/components/styled";
import { ReactNode, useRef } from "react";
import { removeTrailZero } from "@/utils/numberFormatting";
import { usePreventNumberInputScroll } from "@/hooks/usePreventNumberInputScroll";

type FormNumberInputProps = InputProps & {
  name: string;
  control: Control<any, any>;
  label?: ReactNode;
  adornment?: ReactNode;
  controlProps?: Partial<ControllerProps>;
  /** Whether to automatically format the number to remove trailing zeros on blur */
  autoFormat?: boolean;
};

const BoxIcon = styled(Box)(() => ({
  position: "absolute",
  right: "15px",
  top: 0,
  transform: "translateY(50%)",
  display: "flex",
  cursor: "pointer",
}));

const FormNumberInput = ({
  name,
  control,
  label,
  controlProps,
  type = "number",
  adornment,
  autoFormat = true,
  ...props
}: FormNumberInputProps) => {
  const { palette } = useTheme();
  const isRequired = !!controlProps?.rules?.required;

  const formState = useFormState({ control });
  const shouldShowError = formState.isSubmitted ?? true;

  const inputRef = useRef<HTMLInputElement>(null);
  usePreventNumberInputScroll(inputRef);

  return (
    <FormControl component="fieldset" sx={{ width: "100%" }}>
      <FormLabel component="legend" sx={{ width: "100%" }}>
        <Box
          sx={{
            fontSize: "12px",
            fontWeight: 500,
            color: (theme) => theme.palette.textGray1,
            paddingBottom: "2px",
            display: "flex",
          }}
        >
          {label}{" "}
          {isRequired && (
            <Typography sx={{ color: "red", paddingLeft: "4px" }}>*</Typography>
          )}
        </Box>
      </FormLabel>
      <Controller
        name={name}
        control={control}
        {...controlProps}
        render={({
          field: { onChange, value, onBlur },
          fieldState: { error, isTouched },
        }) => {
          const hasValidValue =
            typeof value === "number" ? value > 0 : Number(value) > 0;
          const showError =
            (shouldShowError || isTouched) && !!error && !hasValidValue;

          const handleBlur = (event: React.FocusEvent<HTMLInputElement>) => {
            onBlur();
            if (autoFormat && value) {
              const formatted = removeTrailZero(value);
              if (formatted !== value?.toString()) {
                onChange(formatted);
              }
            }

            if (props.onBlur) {
              props.onBlur(event);
            }
          };

          return (
            <Box sx={{ position: "relative", width: "100%" }}>
              <StyledInput
                error={showError}
                onChange={onChange}
                onBlur={handleBlur}
                value={value || ""}
                fullWidth
                adornment={!!adornment}
                type={type}
                {...props}
                inputRef={inputRef}
              />
              {showError && !!error?.message && (
                <Typography
                  sx={{
                    fontSize: "12px",
                    color: palette.errorText,
                    paddingTop: "4px",
                  }}
                >
                  {error?.message}
                </Typography>
              )}
              {!!adornment && <BoxIcon>{adornment}</BoxIcon>}
            </Box>
          );
        }}
      />
    </FormControl>
  );
};

export default FormNumberInput;
